import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MarketingNav, Footer } from "@/components/Marketing";
import { SlashMark } from "@/components/Logo";
import { api, mediaUrl } from "@/lib/api";
import { themeOf } from "@/lib/themes";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Eye, Layers } from "lucide-react";

export default function Profile() {
  const { username } = useParams();
  const [p, setP] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get(`/profile/${username}`); setP(data); }
      catch { setErr(true); }
    })();
  }, [username]);

  if (err) return (
    <div className="bg-background min-h-screen"><MarketingNav />
      <div className="max-w-3xl mx-auto px-5 py-24 text-center">
        <SlashMark size={40} color="hsl(var(--primary))" className="mx-auto mb-4" />
        <h1 className="font-display text-2xl font-semibold text-foreground">Profile not found</h1>
        <Link to="/discover"><Button className="mt-6 rounded-full">Explore timelines</Button></Link>
      </div>
    </div>
  );
  if (!p) return <div className="min-h-screen grid place-items-center bg-background"><div className="animate-pulse"><SlashMark size={40} color="hsl(var(--primary))" /></div></div>;

  return (
    <div className="bg-background min-h-screen">
      <MarketingNav />
      {/* Header */}
      <div className="relative grain border-b border-border">
        <div className="absolute inset-0 opacity-[0.08]" style={{ background: "radial-gradient(600px 300px at 20% -20%, #3A1F6E, transparent)" }} aria-hidden />
        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-16 relative">
          <div className="flex flex-col sm:flex-row sm:items-end gap-6">
            <Avatar className="h-24 w-24 border-4 border-card shadow-lg">
              {p.avatar ? <img src={mediaUrl(p.avatar)} alt={p.name} /> : <AvatarFallback className="bg-primary text-primary-foreground text-3xl font-display">{(p.name || "U").slice(0, 1).toUpperCase()}</AvatarFallback>}
            </Avatar>
            <div className="flex-1">
              <h1 className="font-display text-4xl font-semibold tracking-tight text-foreground">{p.name}</h1>
              <div className="text-muted-foreground">@{p.username}</div>
              {p.bio && <p className="mt-3 text-foreground/80 max-w-xl">{p.bio}</p>}
              <div className="mt-4 flex gap-6 text-sm">
                <div><span className="font-display font-semibold text-foreground">{p.timeline_count}</span> <span className="text-muted-foreground">timelines</span></div>
                <div><span className="font-display font-semibold text-foreground">{p.total_views.toLocaleString()}</span> <span className="text-muted-foreground">total views</span></div>
                <div><span className="font-display font-semibold text-foreground capitalize">{p.subscription}</span> <span className="text-muted-foreground">plan</span></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-5 sm:px-8 py-12">
        {p.collections?.length > 0 && (
          <div className="mb-12">
            <h2 className="font-display text-xl font-semibold text-foreground mb-6">Collections</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {p.collections.map((col) => (
                <Link key={col.slug} to={`/c/${p.username}/${col.slug}`} data-testid={`profile-col-${col.slug}`}
                  className="group rounded-2xl border border-border bg-card overflow-hidden transition-transform duration-300 hover:-translate-y-1">
                  <div className="h-24 bg-primary grain grid place-items-center text-primary-foreground"><Layers size={26} /></div>
                  <div className="p-5">
                    <h3 className="font-display font-semibold text-foreground group-hover:text-primary transition-colors">{col.title}</h3>
                    <p className="mt-1 text-sm text-muted-foreground line-clamp-2">{col.description || "A curated set of timelines"}</p>
                    <div className="mt-2 text-xs text-muted-foreground">{col.count} timelines</div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
        <h2 className="font-display text-xl font-semibold text-foreground mb-6">Published timelines</h2>
        {p.timelines.length === 0 ? (
          <p className="text-muted-foreground">No public timelines yet.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {p.timelines.map((tl, i) => {
              const t = themeOf(tl.theme);
              return (
                <motion.div key={tl.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}>
                  <Link to={`/t/${p.username}/${tl.slug}`} data-testid={`profile-tl-${tl.slug}`}
                    className="group block rounded-2xl border border-border bg-card overflow-hidden transition-transform duration-300 hover:-translate-y-1">
                    <div className="h-40 relative overflow-hidden" style={{ background: t.bgGradient }}>
                      {tl.cover_image && <img src={mediaUrl(tl.cover_image)} alt="" className="absolute inset-0 w-full h-full object-cover opacity-85 transition-transform duration-500 group-hover:scale-105" loading="lazy" />}
                      <span className="absolute top-3 left-3 text-[11px] px-2 py-0.5 rounded-full glass font-medium" style={{ color: t.text }}>{tl.category}</span>
                    </div>
                    <div className="p-5">
                      <h3 className="font-display font-semibold text-foreground group-hover:text-primary transition-colors">{tl.title}</h3>
                      <p className="mt-1.5 text-sm text-muted-foreground line-clamp-2">{tl.description}</p>
                      <div className="mt-3 flex items-center gap-1 text-xs text-muted-foreground"><Eye size={13} /> {tl.views.toLocaleString()} · {tl.event_count} events</div>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
